import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Box, Title, Text, Link } from '@nerdwallet/currency';
import Image from '@nerdwallet/react-image';
// eslint-disable-next-line lodash/import-scope
import property from 'lodash/property';

import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import parseMarkdownLinks from '../../lib/parse-md-links';
import BackgroundColor from '../background-color/background-color';
import getTextColor from '../../lib/text-color-config';
import {
  boolCheckboxSpec,
  group,
  numberInputSpec,
  repeatable,
  stringInputSpec,
  stringSelectSpec,
} from '../../lib/editing-configs';
import styles from './jump-link-nav.module.less';
import { getTextColorStyle } from '../../lib/helpers';

const navigationItemMaxWidth = 154;
const columnsLayoutValues = [
  { label: 'Select', value: '' },
  { label: '1', value: '1' },
  { label: '2', value: '2' },
  { label: '3', value: '3' },
  { label: '4', value: '4' },
];

const linkParser = (content) => parseMarkdownLinks(content, false);

const htmlOutputParser = (content, enableHtml = false) => {
  if (enableHtml) {
    // eslint-disable-next-line react/no-danger
    return <span dangerouslySetInnerHTML={{ __html: content }} />;
  }
  return linkParser(content);
};

const NavigationItemImage = ({ imageUrl, imageRolloverUrl, alt, url }) => {
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);

  if (!imageUrl) {
    return null;
  }

  return (
    <div onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      <Link href={url}>
        <Image
          naturalHeight={73}
          naturalWidth={navigationItemMaxWidth}
          backgroundColor="transparent"
          src={isHovered && imageRolloverUrl ? imageRolloverUrl : imageUrl}
          alt={alt}
        />
      </Link>
    </div>
  );
};

const JumpLinkNav = ({
  bgOptions,
  title,
  titleSize,
  titleColorOptions,
  subtitle,
  subtitleColorOptions,
  subtitleSize,
  titleChronicle,
  navigationItems,
  dangerouslyAllowHTMLInSubtitle,
  columnsLayoutDesktop,
  columnsLayoutMobile,
  isolatedFirstRowInMobile,
  size,
  itemsHeight,
  itemsTopMarginDesktop,
  itemsTopMarginMobile,
  itemsBottomMarginDesktop,
  itemsBottomMarginMobile,
  itemsRowSpacing,
  subtitleBottomMarginDesktop,
  subtitleBottomMarginMobile,
  itemsGapMobile,
}) => {
  const getColumnsLayout = useCallback(() => {
    if (size !== 'lg') {
      return columnsLayoutMobile ?? 2;
    }
    return columnsLayoutDesktop ?? 4;
  }, [columnsLayoutDesktop, columnsLayoutMobile, size]);

  const getMobileColumnLayout = (itemIndex) =>
    isolatedFirstRowInMobile && size !== 'lg' && itemIndex === 0
      ? '1 / -1'
      : 'unset';

  const getItemsMargin = () => {
    const margins = { top: null, bottom: null };
    if (size !== 'lg') {
      margins.top = itemsTopMarginMobile;
      margins.bottom = itemsBottomMarginMobile;
      return margins;
    }
    margins.top = itemsTopMarginDesktop;
    margins.bottom = itemsBottomMarginDesktop;
    return margins;
  };

  const titleMarginBottom =
    size !== 'lg' ? subtitleBottomMarginMobile : subtitleBottomMarginDesktop;

  return (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => {
        return (
          <div className={styles.container} style={{ backgroundColor }}>
            <Title
              size={titleSize}
              style={getTextColorStyle(titleColorOptions)}
              className={classNames([
                styles.title,
                {
                  [styles.inChronicle]: titleChronicle,
                },
              ])}
              id="title"
            >
              {title}
            </Title>

            <Box
              className={styles.subtitleBox}
              style={{
                ...getTextColorStyle(subtitleColorOptions),
                marginBottom: titleMarginBottom,
              }}
            >
              <Text size={subtitleSize} id="subtitle">
                {htmlOutputParser(subtitle, dangerouslyAllowHTMLInSubtitle)}
              </Text>
            </Box>
            <Box
              className={styles.navigationItemsBox}
              style={{
                gridTemplateColumns: `repeat(${getColumnsLayout()}, 1fr)`,
                rowGap: itemsRowSpacing,
                columnGap: size === 'sm' ? itemsGapMobile : null,
                marginTop: getItemsMargin().top,
                marginBottom: getItemsMargin().bottom,
              }}
            >
              {navigationItems.map((navigationItem, index) => (
                <Box
                  key={navigationItem.$unique_key}
                  style={{
                    gridColumn: getMobileColumnLayout(index),
                    maxWidth:
                      navigationItem.imageMaxWidth || navigationItemMaxWidth,
                    height: itemsHeight,
                  }}
                >
                  <NavigationItemImage
                    imageUrl={navigationItem.imageUrl}
                    imageRolloverUrl={navigationItem.imageRolloverUrl}
                    alt={navigationItem.alt || 'Navigation Image'}
                    url={navigationItem.url}
                  />
                </Box>
              ))}
            </Box>
          </div>
        );
      }}
    </BackgroundColor>
  );
};

JumpLinkNav.displayName = 'JumpLinkNav';

NavigationItemImage.propTypes = {
  imageUrl: PropTypes.string,
  imageRolloverUrl: PropTypes.string,
  alt: PropTypes.string,
  url: PropTypes.string,
  children: PropTypes.node,
};

NavigationItemImage.defaultProps = {
  children: null,
};

JumpLinkNav.propTypes = {
  ...BackgroundColor.propTypes,
  title: PropTypes.string,
  titleSize: PropTypes.oneOf(['default', 'small']),
  subtitle: PropTypes.string,
  subtitleSize: PropTypes.oneOf(['default', 'large', 'small', 'x-small']),
  navigationItems: PropTypes.array,
  dangerouslyAllowHTMLInSubtitle: PropTypes.bool,
  children: PropTypes.node,
};

JumpLinkNav.defaultProps = {
  ...BackgroundColor.defaultProps.bgOptions,
  title: '',
  titleSize: 'default',
  subtitle: '',
  subtitleSize: 'default',
  dangerouslyAllowHTMLInSubtitle: false,
  navigationItems: [],
  children: null,
};

JumpLinkNav.getEditingConfig = (componentDocument) => [
  ...BackgroundColor.getEditingConfig(componentDocument),
  group('Title', [
    stringInputSpec('title', 'Title'),
    stringSelectSpec(
      `titleSize`,
      'Title Size',
      [
        { label: 'Default', value: 'default' },
        { label: 'Small', value: 'small' },
      ],
      {
        help: 'Defaults to default',
      }
    ),
    boolCheckboxSpec('titleChronicle', 'Title in Chronicle?'),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'titleColorOptions',
      'Title Color'
    ),
  ]),
  group('Subtitle', [
    stringInputSpec('subtitle', 'Subtitle'),
    stringSelectSpec(
      `subtitleSize`,
      'Subtitle Size',
      [
        { label: 'Default', value: 'default' },
        { label: 'Large', value: 'large' },
        { label: 'Small', value: 'small' },
        { label: 'X-Small', value: 'x-small' },
      ],
      {
        help: 'Defaults to default',
      }
    ),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'subtitleColorOptions',
      'Subtitle Color'
    ),
    numberInputSpec(
      'subtitleBottomMarginDesktop',
      'Subtitle Bottom Margin Desktop'
    ),
    numberInputSpec(
      'subtitleBottomMarginMobile',
      'Subtitle Bottom Margin Mobile'
    ),
    boolCheckboxSpec(
      'dangerouslyAllowHTMLInSubtitle',
      'Allow HTML in Subtitle?',
      {
        help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
      }
    ),
  ]),
  group('Link items layout', [
    stringSelectSpec(
      `columnsLayoutDesktop`,
      'Columns Layout desktop',
      columnsLayoutValues,
      {
        help: 'defaults to "4"',
      }
    ),
    stringSelectSpec(
      `columnsLayoutMobile`,
      'Columns Layout Mobile',
      columnsLayoutValues,
      {
        help: 'defaults to "2"',
      }
    ),
    boolCheckboxSpec(
      'isolatedFirstRowInMobile',
      'Isolated First Row in Mobile?'
    ),
    numberInputSpec('itemsHeight', 'Items Height'),
    numberInputSpec('itemsTopMarginDesktop', 'Items Top Margin Desktop'),
    numberInputSpec('itemsTopMarginMobile', 'Items Top Margin Mobile'),
    numberInputSpec('itemsBottomMarginDesktop', 'Items Bottom Margin Desktop'),
    numberInputSpec('itemsBottomMarginMobile', 'Items Bottom Margin Mobile'),
    numberInputSpec('itemsRowSpacing', 'Items Row Spacing'),
    numberInputSpec('itemsGapMobile', 'Items Horizontal Space In Mobile'),
  ]),
  repeatable('navigationItems', 'Navigation Items', 'Navigation Item', () => [
    stringInputSpec('altText', 'Alternate Image text'),
    stringInputSpec('imageUrl', 'Image URL'),
    stringInputSpec('imageRolloverUrl', 'Image Rollover URL'),
    stringInputSpec('url', 'URL'),
    stringInputSpec('imageMaxWidth', 'Image Max Width', {
      help: 'Default to 154px',
    }),
  ]),
];

export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(JumpLinkNav);
